import { t } from '@lingui/macro';

// Template header row (PD-3691). Keep in sync with Depots::BulkCsvImporter.
export const TEMPLATE_HEADERS = [
  'Account*',
  'FMS ID*',
  'Name*',
  'Email*',
  'Address',
  'Country*',
  'State*',
  'City*',
  'Postal Code',
  'Notes',
  'Primary Contact Name',
  'Contact Email',
  'Contact Phone',
  'Notification Preference',
];

export const TEMPLATE_FILENAME = 'add_depot_template.csv';

// UX-only limits; keep aligned with the API (MAX_FILE_SIZE_BYTES / MAX_ROWS).
export const MAX_FILE_BYTES = 2 * 1024 * 1024;
export const MAX_DATA_ROWS = 10_000;

export const downloadDepotTemplate = () => {
  const blob = new Blob([`${TEMPLATE_HEADERS.join(',')}\n`], {
    type: 'text/csv;charset=utf-8;',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = TEMPLATE_FILENAME;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// FileReader, not Blob.text(), so it also works under jsdom.
const readText = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });

// Split into CSV records, respecting quoted fields (commas/newlines) so the row count
// matches the backend parse.
const splitCsvRecords = (text: string): string[] => {
  const records: string[] = [];
  let field = '';
  let inQuotes = false;

  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];

    if (char === '"') {
      if (inQuotes && text[i + 1] === '"') {
        field += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (!inQuotes && (char === '\n' || char === '\r')) {
      if (char === '\r' && text[i + 1] === '\n') i += 1;
      records.push(field);
      field = '';
    } else {
      field += char;
    }
  }

  if (field !== '') records.push(field);
  return records;
};

// Ignore records with only commas/whitespace (e.g. Excel filler rows).
const hasData = (record: string) => record.replace(/,/g, '').trim() !== '';

const normalizeHeader = (header: string) =>
  header.replace(/\*/g, '').trim().toLowerCase();

// Returns a user-facing error message, or null when acceptable. Size is checked before
// reading so a huge file isn't loaded into memory.
export const validateDepotCsv = async (file: File): Promise<string | null> => {
  const isCsv =
    file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv');
  if (!isCsv) return t`Please upload a .csv file.`;

  if (file.size > MAX_FILE_BYTES) {
    return t`The file exceeds the maximum size of 2 MB.`;
  }

  const records = splitCsvRecords(await readText(file)).filter(hasData);
  if (records.length === 0) return t`The file is empty.`;

  const headers = records[0].split(',').map(normalizeHeader);
  if (!headers.includes('account')) {
    return t`The file is missing the required Account column.`;
  }

  const dataRows = records.length - 1;
  if (dataRows === 0) return t`The file has no depot rows.`;
  if (dataRows > MAX_DATA_ROWS) {
    return t`The file exceeds the maximum of 10,000 rows.`;
  }

  return null;
};
